import React, { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import {
  FileText, Search, Eye, Check, X, Trash2, AlertTriangle,
  ChevronLeft, ChevronRight, Clock, User, Calendar, Tag,
  Play, Image, MessageSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/admin';
const PUBLIC_ORIGIN = (process.env.REACT_APP_PUBLIC_ORIGIN || 'https://naoview.app').replace(/\/+$/, '');
const MEDIA_BASE = `${PUBLIC_ORIGIN}/create/media`;

const EMPTY_VALUES = new Set([
  '',
  'none',
  'null',
  'undefined',
  'camera_new.png',
  'default.png',
  'default-avatar.png',
  'avatar.png'
]);

const cleanValue = (value) => String(value ?? '').trim();

const firstValue = (...values) =>
  values.map(cleanValue).find((value) => value && !EMPTY_VALUES.has(value.toLowerCase())) || '';

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(cleanValue(value));

const isHlsSource = (value) =>
  cleanValue(value).toLowerCase().split(/[?#]/)[0].endsWith('.m3u8');

const buildMediaUrl = (file) => {
  const value = cleanValue(file);
  if (!value) return null;
  if (isAbsoluteUrl(value)) return value;

  const withoutLeadingSlash = value.replace(/^\/+/, '');

  if (withoutLeadingSlash.startsWith('create/media/')) {
    return `${PUBLIC_ORIGIN}/${withoutLeadingSlash}`;
  }

  return `${MEDIA_BASE}/${withoutLeadingSlash}`;
};

const buildAvatarCandidates = (avatar) => {
  const value = cleanValue(avatar);
  if (!value || EMPTY_VALUES.has(value.toLowerCase())) return [];

  if (isAbsoluteUrl(value)) return [value];

  const path = value.replace(/^\/+/, '');

  if (
    path.startsWith('avatars/') ||
    path.startsWith('uploads/') ||
    path.startsWith('create/media/')
  ) {
    return [`${PUBLIC_ORIGIN}/${path}`];
  }

  return uniqueValues([
    `${PUBLIC_ORIGIN}/avatars/${path}`,
    `${PUBLIC_ORIGIN}/uploads/avatars/${path}`,
    `${PUBLIC_ORIGIN}/uploads/profile_pictures/${path}`,
    `${PUBLIC_ORIGIN}/create/media/${path}`
  ]);
};

const getOwnerAvatar = (event = {}, payload = {}) =>
  firstValue(
    event.owner_avatar,
    event.owner_profile_picture,
    event.profile_picture,
    event.avatar,
    event.photo,
    payload.owner_avatar,
    payload.profile_picture,
    payload.owner?.owner_avatar,
    payload.owner?.profile_picture,
    payload.owner?.avatar,
    payload.user?.profile_picture,
    payload.user?.avatar,
    payload.user?.photo
  );

const getOwnerName = (event = {}, payload = {}) => {
  const directName = firstValue(
    event.owner_name,
    event.owner_full_name,
    event.full_name,
    payload.owner_name,
    payload.owner?.name,
    payload.owner?.full_name,
    payload.user?.name,
    payload.user?.full_name
  );

  if (directName) return directName;

  const firstName = firstValue(
    event.owner_first_name,
    event.first_name,
    payload.owner?.first_name,
    payload.user?.first_name
  );

  const lastName = firstValue(
    event.owner_last_name,
    event.last_name,
    payload.owner?.last_name,
    payload.user?.last_name
  );

  return `${firstName} ${lastName}`.trim() || 'Unknown User';
};

function AvatarImage({ avatar, name, className = 'w-full h-full object-cover' }) {
  const candidates = buildAvatarCandidates(avatar);
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [avatar]);

  const src = candidates[candidateIndex];
  const initial = cleanValue(name).charAt(0).toUpperCase() || 'U';

  if (!src) {
    return <span className="text-white font-bold">{initial}</span>;
  }

  return (
    <img
      src={src}
      alt={name || 'User'}
      className={className}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setCandidateIndex((index) => index + 1)}
    />
  );
}

function HlsVideoPlayer({ src, poster }) {
  const videoRef = useRef(null);
  const [retryKey, setRetryKey] = useState(0);
  const [playerState, setPlayerState] = useState({
    loading: true,
    error: ''
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    let hls = null;
    let disposed = false;
    let networkRetries = 0;
    let mediaRecoveryAttempted = false;

    setPlayerState({ loading: true, error: '' });

    const markReady = () => {
      if (!disposed) {
        setPlayerState({ loading: false, error: '' });
      }
    };

    const markError = (message) => {
      if (!disposed) {
        setPlayerState({
          loading: false,
          error: message || 'The video could not be loaded.'
        });
      }
    };

    const resetVideo = () => {
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (_) {
        // Ignore cleanup failures.
      }
    };

    const handleNativeError = () => {
      const mediaError = video.error;
      const message = mediaError?.message
        ? `Video error: ${mediaError.message}`
        : 'The browser could not load this video. Check the media URL and server response.';
      markError(message);
    };

    video.addEventListener('loadedmetadata', markReady);
    video.addEventListener('canplay', markReady);
    video.addEventListener('error', handleNativeError);

    // Plain MP4/WebM/MOV files must not be passed to hls.js.
    if (!isHlsSource(src)) {
      video.src = src;
      video.load();

      return () => {
        disposed = true;
        video.removeEventListener('loadedmetadata', markReady);
        video.removeEventListener('canplay', markReady);
        video.removeEventListener('error', handleNativeError);
        resetVideo();
      };
    }

    // Safari and other browsers with native HLS support.
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.load();

      return () => {
        disposed = true;
        video.removeEventListener('loadedmetadata', markReady);
        video.removeEventListener('canplay', markReady);
        video.removeEventListener('error', handleNativeError);
        resetVideo();
      };
    }

    if (!Hls.isSupported()) {
      markError('HLS playback is not supported in this browser.');
      return () => {
        disposed = true;
        video.removeEventListener('loadedmetadata', markReady);
        video.removeEventListener('canplay', markReady);
        video.removeEventListener('error', handleNativeError);
        resetVideo();
      };
    }

    hls = new Hls({
      enableWorker: true,
      lowLatencyMode: false,
      capLevelToPlayerSize: true,
      startLevel: -1,
      backBufferLength: 30,
      maxBufferLength: 30
    });

    hls.attachMedia(video);

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (!disposed) hls.loadSource(src);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, markReady);
    hls.on(Hls.Events.LEVEL_LOADED, markReady);

    hls.on(Hls.Events.ERROR, (_, data) => {
      console.error('HLS playback error:', data);

      if (!data?.fatal || disposed) return;

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR && networkRetries < 2) {
        networkRetries += 1;
        window.setTimeout(() => {
          if (!disposed) hls.startLoad();
        }, networkRetries * 750);
        return;
      }

      if (data.type === Hls.ErrorTypes.MEDIA_ERROR && !mediaRecoveryAttempted) {
        mediaRecoveryAttempted = true;
        hls.recoverMediaError();
        return;
      }

      const statusCode = data?.response?.code;
      const reason = data?.details || data?.reason || 'unknown HLS error';

      if (statusCode === 404) {
        markError('The HLS playlist or one of its video segments was not found (404).');
      } else if (statusCode === 403) {
        markError('The media server refused access to this video (403).');
      } else if (statusCode === 0 || data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        markError(
          'The HLS request was blocked or could not reach the media server. Check CORS and the segment URLs in the playlist.'
        );
      } else {
        markError(`HLS playback failed: ${reason}.`);
      }

      hls.destroy();
      hls = null;
    });

    return () => {
      disposed = true;
      video.removeEventListener('loadedmetadata', markReady);
      video.removeEventListener('canplay', markReady);
      video.removeEventListener('error', handleNativeError);

      if (hls) {
        hls.destroy();
        hls = null;
      }

      resetVideo();
    };
  }, [src, retryKey]);

  return (
    <div className="relative bg-black">
      <video
        ref={videoRef}
        controls
        playsInline
        preload="metadata"
        poster={poster || undefined}
        className="w-full bg-black object-contain"
        style={{
          width: '100%',
          maxHeight: '60vh',
          minHeight: 320,
          aspectRatio: '16 / 9',
          display: 'block'
        }}
      />

      {playerState.loading && !playerState.error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/45 pointer-events-none">
          <div className="px-4 py-2 rounded-full bg-zinc-900/90 border border-zinc-700 text-zinc-300 text-sm">
            Loading video�
          </div>
        </div>
      )}

      {playerState.error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/85 p-6">
          <div className="max-w-md text-center">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <p className="text-white font-semibold">Video preview unavailable</p>
            <p className="text-zinc-400 text-sm mt-2">{playerState.error}</p>
            <button
              type="button"
              onClick={() => setRetryKey((key) => key + 1)}
              className="mt-4 px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 transition-all"
            >
              Retry playback
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminEventsPage() {
  const { token, hasPermission } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState({ status: '', reason: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [pagination.page, statusFilter, search]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pagination.page, limit: 15, status: statusFilter, search }
      });
      const responseEvents = Array.isArray(res.data?.events) ? res.data.events : [];
      setEvents(responseEvents.map((event) => ({
        ...event,
        owner_avatar: getOwnerAvatar(event),
        owner_name: getOwnerName(event)
      })));
      setPagination(res.data?.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  //   const fetchEvents = async () => {
  //   try {
  //     const res = await axios.get(`${API}/events/list`, {
  //       headers: { Authorization: `Bearer ${token}` },
  //       params: { page: pagination.page, limit: 15 } // removed status & search
  //     });
  //     setEvents(res.data.events || []);
  //     setPagination(res.data.pagination);
  //   } catch (err) {
  //     toast.error('Failed to load events');
  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/events/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const viewEvent = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = res.data || {};
      const event = payload.event || {};

      setSelectedEvent({
        ...payload,
        event: {
          ...event,
          owner_avatar: getOwnerAvatar(event, payload),
          owner_name: getOwnerName(event, payload)
        }
      });
    } catch (err) {
      toast.error('Failed to load event details');
    }
  };

  const handleReview = async () => {
    if (!selectedEvent || !reviewAction.status) return;

    try {
      await axios.put(`${API}/events/${selectedEvent.event.id}/review`, {
        status: reviewAction.status,
        reason: reviewAction.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Event ${reviewAction.status} successfully`);
      setShowReviewModal(false);
      setReviewAction({ status: '', reason: '' });
      setSelectedEvent(null);
      fetchEvents();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to review event');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    try {
      await axios.delete(`${API}/events/${selectedEvent.event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Event deleted successfully');
      setShowDeleteModal(false);
      setSelectedEvent(null);
      fetchEvents();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete event');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">Approved</span>;
      case 'rejected':
        return <span className="px-2.5 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">Rejected</span>;
      default:
        return <span className="px-2.5 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">Pending</span>;
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return <Play className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const isVideoFile = (file) => {
    const value = String(file || '').toLowerCase().split('?')[0];

    return (
      value.endsWith('.mp4') ||
      value.endsWith('.webm') ||
      value.endsWith('.mov') ||
      value.endsWith('.m4v') ||
      value.endsWith('.ogg') ||
      value.endsWith('.m3u8')
    );
  };

  const isHlsFile = (file) => {
    return String(file || '')
      .toLowerCase()
      .includes('.m3u8');
  };

  const getMediaFile = (event = {}) => {
    const candidates = [
      event.event_resource,
      event.video_url,
      event.video,
      event.event_video,
      event.video_file,
      event.media_url,
      event.media,
      event.event_media,
      event.file_url,
      event.file,
      event.filename,
      event.resource_url,
      event.content_url,
      event.cover_video,
      event.post_video,
      event.uploaded_file
    ];

    const explicitMedia = candidates.find((value) => String(value || '').trim());
    if (explicitMedia) return explicitMedia;

    // Some older uploads stored the actual media file in cover_photo.
    if (isVideoFile(event.cover_photo)) return event.cover_photo;

    return null;
  };

  const getMediaUrl = (event) => buildMediaUrl(getMediaFile(event));

  const getCoverUrl = (event = {}) => {
    if (!event.cover_photo || isVideoFile(event.cover_photo)) return null;
    return buildMediaUrl(event.cover_photo);
  };

  const isVideoEvent = (event = {}) => {
    const type = String(event.type || event.category || '').toLowerCase();
    const media = getMediaFile(event);

    return type.includes('video') || isVideoFile(media);
  };

  const canReviewEvents = hasPermission('events.approve') || hasPermission('streams.monitor');
  const canManageEvents = hasPermission('events.manage') || hasPermission('streams.terminate');

  const beginReview = (status) => {
    setReviewAction({ status, reason: '' });
    setShowReviewModal(true);
  };

  return (
    <div className="space-y-6" data-testid="admin-events-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Events & Posts</h1>
          <p className="text-zinc-500 text-sm mt-1">Review and manage user-submitted content</p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <button
          onClick={() => { setStatusFilter('all'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'all' ? 'border-indigo-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Total Events</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
        </button>
        <button
          onClick={() => { setStatusFilter('pending'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'pending' ? 'border-amber-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Pending Review</p>
          <p className="text-2xl font-bold text-amber-400 mt-1">{stats.pending}</p>
        </button>
        <button
          onClick={() => { setStatusFilter('approved'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'approved' ? 'border-emerald-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Approved</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.approved}</p>
        </button>
        <button
          onClick={() => { setStatusFilter('rejected'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'rejected' ? 'border-red-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Rejected</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{stats.rejected}</p>
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination(p => ({ ...p, page: 1 }));
            }}
            placeholder="Search events by title, owner, or category..."
            className="w-full pl-10 pr-4 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-indigo-500"
          />
        </div>
      </div>

      {/* Events list */}
      <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Event</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Owner</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Category</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Status</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Date</th>
                <th className="text-right px-6 py-4 text-zinc-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-6 py-4">
                      <div className="h-16 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : events.length > 0 ? (
                events.map((event) => (
                  <tr key={event.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden">
                          {getCoverUrl(event) ? (
                            <img
                              src={getCoverUrl(event)}
                              alt={event.event_data}
                              className="w-12 h-12 object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            getContentIcon(event.type)
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium truncate max-w-[200px]">
                            {event.event_data || 'Untitled Event'}
                          </p>
                          <p className="text-zinc-500 text-xs flex items-center gap-1">
                            <Tag className="w-3 h-3" /> {event.type || 'post'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                          <AvatarImage
                            avatar={getOwnerAvatar(event)}
                            name={getOwnerName(event)}
                            className="w-8 h-8 object-cover"
                          />
                        </div>
                        <div>
                          <p className="text-zinc-300 text-sm">{getOwnerName(event)}</p>
                          <p className="text-zinc-600 text-xs truncate max-w-[120px]">{event.event_owner}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-400 text-sm">{event.category || 'General'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(event.review)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-500 text-sm">
                        {event.DateCreated ? new Date(event.DateCreated).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewEvent(event.id)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-all"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {canReviewEvents && (!event.review || event.review === 'pending') && (
                          <>
                            <button
                              onClick={() => {
                                viewEvent(event.id);
                                setTimeout(() => {
                                  setReviewAction({ status: 'approved', reason: '' });
                                  setShowReviewModal(true);
                                }, 300);
                              }}
                              className="p-2 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-all"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                viewEvent(event.id);
                                setTimeout(() => {
                                  setReviewAction({ status: 'rejected', reason: '' });
                                  setShowReviewModal(true);
                                }, 300);
                              }}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {canManageEvents && (
                          <button
                            onClick={() => {
                              viewEvent(event.id);
                              setTimeout(() => setShowDeleteModal(true), 300);
                            }}
                            className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-zinc-500">
                    No events found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm">
              Page {pagination.page} of {pagination.pages} ({pagination.total} events)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page <= 1}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Event Preview Modal */}
      {selectedEvent && !showReviewModal && !showDeleteModal && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-50 p-3 sm:p-6">
          <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-3xl max-w-6xl w-full max-h-[92vh] border border-zinc-800 shadow-2xl overflow-hidden flex flex-col">
            <div className="sticky top-0 z-10 px-5 sm:px-6 py-4 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-300 text-xs font-semibold border border-indigo-500/20">
                      <Eye className="w-3.5 h-3.5" /> Content Review
                    </span>
                    {getStatusBadge(selectedEvent.event.review)}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white truncate" style={{ fontFamily: 'Manrope' }}>
                    {selectedEvent.event.event_data || 'Untitled Event'}
                  </h2>
                  <p className="text-zinc-500 text-sm mt-1">Watch or inspect the submitted content before making a review decision.</p>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="shrink-0 w-10 h-10 rounded-full bg-zinc-800/80 text-zinc-400 hover:text-white hover:bg-zinc-700 transition-all flex items-center justify-center"
                  title="Close preview"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.75fr)] gap-6">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden bg-black border border-zinc-800 shadow-xl">
                    {getMediaUrl(selectedEvent.event) && isVideoEvent(selectedEvent.event) ? (
                      <HlsVideoPlayer
                        src={getMediaUrl(selectedEvent.event)}
                        poster={getCoverUrl(selectedEvent.event)}
                      />
                    ) : getCoverUrl(selectedEvent.event) ? (
                      <img
                        src={getCoverUrl(selectedEvent.event)}
                        alt="Cover"
                        className="w-full max-h-[68vh] object-contain bg-black"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="h-72 flex flex-col items-center justify-center gap-3 text-zinc-500">
                        <FileText className="w-10 h-10" />
                        <span>No media preview available</span>
                      </div>
                    )}
                  </div>

                  {getMediaUrl(selectedEvent.event) && (
                    <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3">
                      <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-1">Media file</p>
                      <p className="text-zinc-400 text-sm break-all">{getMediaUrl(selectedEvent.event)}</p>
                    </div>
                  )}
                </div>

                <aside className="space-y-4">
                  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-5">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-indigo-300" /> Submission Details
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-zinc-500">Category</span>
                        <span className="text-zinc-300 font-medium text-right">{selectedEvent.event.category || 'General'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-zinc-500">Type</span>
                        <span className="text-zinc-300 font-medium text-right">{selectedEvent.event.type || (isVideoEvent(selectedEvent.event) ? 'Video' : 'Post')}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-zinc-500">Price</span>
                        <span className="text-amber-400 font-bold text-right">{selectedEvent.event.event_price || 'Free'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-zinc-500">Created</span>
                        <span className="text-zinc-300 text-right">{selectedEvent.event.DateCreated ? new Date(selectedEvent.event.DateCreated).toLocaleString() : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-zinc-800 bg-zinc-900/70 p-5">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <User className="w-4 h-4 text-indigo-300" /> Owner
                    </h3>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                        <AvatarImage
                          avatar={getOwnerAvatar(selectedEvent.event, selectedEvent)}
                          name={getOwnerName(selectedEvent.event, selectedEvent)}
                          className="w-12 h-12 object-cover"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{getOwnerName(selectedEvent.event, selectedEvent)}</p>
                        <p className="text-zinc-500 text-sm truncate">{selectedEvent.event.event_owner}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-4 text-center">
                      <p className="text-zinc-500 text-xs">Views</p>
                      <p className="text-xl font-bold text-white">{selectedEvent.stats?.views || 0}</p>
                    </div>
                    <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-4 text-center">
                      <p className="text-zinc-500 text-xs">Likes</p>
                      <p className="text-xl font-bold text-white">{selectedEvent.stats?.likes || 0}</p>
                    </div>
                    <div className="bg-zinc-900/70 border border-zinc-800 rounded-2xl p-4 text-center">
                      <p className="text-zinc-500 text-xs">Status</p>
                      <p className="text-xs font-bold text-amber-400 mt-1">{selectedEvent.event.review || 'pending'}</p>
                    </div>
                  </div>

                  {selectedEvent.event.reason_review && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
                      <p className="text-red-400 text-sm font-semibold">Previous review note</p>
                      <p className="text-zinc-400 text-sm mt-1">{selectedEvent.event.reason_review}</p>
                    </div>
                  )}
                </aside>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 px-4 sm:px-6 py-4">
              {canReviewEvents ? (
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <p className="text-zinc-500 text-sm">Review decision is available here after previewing the content.</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => beginReview('rejected')}
                      className="flex-1 sm:flex-none px-5 py-3 bg-red-500/15 text-red-300 font-semibold rounded-xl hover:bg-red-500/25 border border-red-500/25 transition-all flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" /> Reject
                    </button>
                    <button
                      onClick={() => beginReview('approved')}
                      className="flex-1 sm:flex-none px-5 py-3 bg-emerald-500 text-white font-bold rounded-xl hover:bg-emerald-600 shadow-lg shadow-emerald-950/40 transition-all flex items-center justify-center gap-2"
                    >
                      <Check className="w-4 h-4" /> Approve
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">You have view access only. Approval permission is required to review this content.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-[60] p-4">
          <div className={`rounded-3xl max-w-lg w-full border shadow-2xl overflow-hidden ${reviewAction.status === 'approved'
            ? 'bg-gradient-to-b from-emerald-950/70 to-zinc-950 border-emerald-500/30'
            : 'bg-gradient-to-b from-red-950/70 to-zinc-950 border-red-500/30'
            }`}>
            <div className="p-6 border-b border-white/10">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${reviewAction.status === 'approved' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                  }`}>
                  {reviewAction.status === 'approved' ? (
                    <Check className="w-6 h-6 text-emerald-300" />
                  ) : (
                    <X className="w-6 h-6 text-red-300" />
                  )}
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                    {reviewAction.status === 'approved' ? 'Approve this content?' : 'Reject this content?'}
                  </h3>
                  <p className="text-zinc-400 text-sm mt-1 line-clamp-2">{selectedEvent.event.event_data || 'Untitled Event'}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="rounded-2xl bg-black/30 border border-white/10 p-4">
                <p className="text-zinc-400 text-sm">
                  {reviewAction.status === 'approved'
                    ? 'This will make the content visible according to your platform approval rules.'
                    : 'This will mark the content as rejected and store the reason for future reference.'}
                </p>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  {reviewAction.status === 'approved' ? 'Approval note' : 'Rejection reason'}
                  {reviewAction.status === 'approved' && <span className="text-zinc-500 font-normal"> optional</span>}
                </label>
                <textarea
                  value={reviewAction.reason}
                  onChange={(e) => setReviewAction(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={reviewAction.status === 'approved'
                    ? 'Example: Approved after video review.'
                    : 'Example: The video violates content guidelines.'
                  }
                  className="w-full px-4 py-3 bg-zinc-950/70 border border-zinc-700 rounded-2xl text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-500 resize-none"
                  rows={4}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setReviewAction({ status: '', reason: '' });
                  }}
                  className="flex-1 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
                >
                  Back to Preview
                </button>
                <button
                  onClick={handleReview}
                  className={`flex-1 py-3 font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${reviewAction.status === 'approved'
                    ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                >
                  {reviewAction.status === 'approved' ? (
                    <><Check className="w-4 h-4" /> Confirm Approval</>
                  ) : (
                    <><X className="w-4 h-4" /> Confirm Rejection</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-md w-full border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3 text-red-400">
                <AlertTriangle className="w-6 h-6" />
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                  Delete Event
                </h3>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-zinc-400">
                Are you sure you want to permanently delete this event?
                <span className="block mt-2 text-white font-medium">
                  "{selectedEvent.event.event_data}"
                </span>
                <span className="block mt-1 text-zinc-500 text-sm">
                  by {getOwnerName(selectedEvent.event, selectedEvent) || selectedEvent.event.event_owner}
                </span>
              </p>
              <p className="text-red-400 text-sm">This action cannot be undone.</p>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}